import axios from 'axios';
import config from '@/config';
import { getAccessToken, clearAccessToken } from './tokenStorage';
import { refreshAccessToken } from './refreshToken';

const apiClient = axios.create({
  baseURL: config.apiUrl,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.request.use(
  (requestConfig) => {
    const token = getAccessToken();
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }
    return requestConfig;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;

    if (
      status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/refresh')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const payload = await refreshAccessToken();
        const newToken = payload.accessToken;
        processQueue(null, newToken);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearAccessToken();
        return Promise.reject(normalizeError(refreshError));
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(normalizeError(error));
  }
);

const normalizeError = (error) => {
  const data = error.response?.data;
  const errors = data?.errors || null;
  let message = data?.message || error.message || 'Request failed';

  const firstFieldMsg =
    Array.isArray(errors) && errors.length
      ? errors[0]?.msg || errors[0]?.message
      : null;

  if (
    firstFieldMsg &&
    (!message || /^(validation failed|request failed|bad request|invalid value)$/i.test(String(message)))
  ) {
    message = firstFieldMsg;
  }

  return {
    message,
    status: error.response?.status || 500,
    errors,
  };
};

export default apiClient;
